/**
 * Client-side error capture with deduplication
 * Sends errors to the API worker which will be captured by the tail worker
 */

const reported = new Set<string>();
const MAX_TRACKED_ERRORS = 100;

// API endpoint - adjust based on environment
const API_BASE = import.meta.env.PROD
  ? 'https://wizard-duel-api.neenhouse.workers.dev'
  : 'http://localhost:8788';

export function reportError(
  error: Error | string,
  context?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') return;

  const errorObj = error instanceof Error ? error : new Error(String(error));
  const key = `${errorObj.message}:${errorObj.stack?.slice(0, 100) || ''}`;

  // Deduplicate errors
  if (reported.has(key)) return;
  if (reported.size >= MAX_TRACKED_ERRORS) {
    const firstKey = reported.values().next().value;
    if (firstKey) reported.delete(firstKey);
  }
  reported.add(key);

  fetch(`${API_BASE}/api/logs/client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: errorObj.message,
      stack: errorObj.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...context,
    }),
    keepalive: true,
  }).catch(() => {
    // Silently fail - we don't want error reporting to cause more errors
  });
}

export function initErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  if ((window as { __errorHandlersInit?: boolean }).__errorHandlersInit) return;
  (window as { __errorHandlersInit?: boolean }).__errorHandlersInit = true;

  window.onerror = (message, source, lineno, colno, error) => {
    reportError(error || new Error(String(message)), {
      type: 'onerror',
      source,
      lineno,
      colno,
    });
    return false;
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));
    reportError(error, { type: 'unhandledrejection' });
  };
}
